import { useCallback } from 'react'
import { useHistory, useRouteMatch } from 'react-router-dom'

import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { getArticlePath } from 'utils/urlUtils'
import { useLoading } from './UseLoading'
import { createNewArticle } from 'utils'
import { ALL_FILES_PATH, GROUP_PATH } from 'paths'
import { handleErrorResponseWithMessage } from 'api/utils/handleErrorResponseWithMessage'
import { catchBlockedPopUp } from 'utils/catchBlockedPopUp'
import { useCurrentWorkspace } from './useCurrentWorkspace'
import { CREATE_DOC_EVENT, trackEvent } from 'utils/trackingUtils'
import { AlmanacFile as File } from 'types'
import { useIsPwa } from 'utils/isPwa'
import { sendMessageToRoot } from 'screens/TabRoot/utils'
import { useCurrentUserProfile } from './queries/users/useCurrentUserProfile'

interface CreateArticleProps {
  openInNewTab: boolean
  folder?: File
}

export const useCreateArticle = ({ openInNewTab, folder }: CreateArticleProps) => {
  const workspace = useCurrentWorkspace()
  const { data: profile } = useCurrentUserProfile()
  const history = useHistory()
  const folderRouteMatch = useRouteMatch<any>(ALL_FILES_PATH)
  const teamRouteMatch = useRouteMatch<any>(GROUP_PATH)
  const [createArticle, createArticleLoading] = useLoading(createNewArticle)
  const teamId = teamRouteMatch?.params.id
  const folderId = folder?.id || folderRouteMatch?.params.location
  const isPwa = useIsPwa()

  const createArticleCallback = useCallback(
    async (homepageDoc = false, inNewTab = openInNewTab) => {
      if (!workspace) {
        showErrorMessageToast('Please return to a workspace to create a doc')
        return
      }

      trackEvent(CREATE_DOC_EVENT, profile)

      const response = await createArticle({
        workspaceKey: workspace.key,
        projectId: folderId,
        channelId: teamId,
        homepageDoc,
      }).then(handleErrorResponseWithMessage())

      if (response && response.type === 'success') {
        const path = getArticlePath(response.data.article)
        if (inNewTab && isPwa) {
          sendMessageToRoot({ type: 'AddTab', url: path, switchToTab: true })
        } else if (inNewTab) {
          catchBlockedPopUp(path)
        } else {
          history.push(path)
        }
        return response.data.article
      }
    },
    [createArticle, folderId, teamId, workspace, history, openInNewTab, profile, isPwa],
  )

  return { createArticleCallback, createArticleLoading }
}
